import React, { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import './App.css'

const API_URL = 'https://bandaeventstransfer.live/api'
const eventOptions = ['all', 'Lead', 'Sale', 'install']

const TOKEN_KEY = 'token'

const normalizeValue = (value) => String(value || '').trim().toLowerCase()

const getAppLabel = (app) => {
  if (!app) return 'All Apps'
  if (app.appName && app.fbId) return `${app.appName} (${app.fbId})`
  return app.appName || app.fbId || 'Unknown app'
}

function SearchableAppSelect({ apps, value, selectedFbId, onChange }) {
  const wrapperRef = useRef(null)
  const searchInputRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')

  const selectedApp = useMemo(() => {
    if (selectedFbId) {
      return apps.find(app => String(app.fbId) === String(selectedFbId)) || null
    }

    if (!value || value === 'all') return null

    return apps.find(app => app.appName === value || app.fbId === value) || null
  }, [apps, selectedFbId, value])

  const filteredApps = useMemo(() => {
    const q = normalizeValue(search)

    if (!q) return apps

    return apps.filter(app => (
      normalizeValue(app.appName).includes(q) || normalizeValue(app.fbId).includes(q)
    ))
  }, [apps, search])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false)
        setSearch('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (isOpen) {
      window.requestAnimationFrame(() => searchInputRef.current?.focus())
    }
  }, [isOpen])

  const openMenu = () => {
    setSearch('')
    setIsOpen(true)
  }

  const selectApp = (app) => {
    onChange(app || null)
    setIsOpen(false)
    setSearch('')
  }

  return (
    <div className="app-combobox" ref={wrapperRef}>
      <div className="app-combobox-control">
        <button
          className={`app-combobox-trigger ${isOpen ? 'is-open' : ''}`}
          type="button"
          onClick={openMenu}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          {selectedApp && value !== 'all' ? (
            <span className="app-combobox-selected" title={`${selectedApp.appName || 'Unknown app'} • fbId: ${selectedApp.fbId || '-'}`}>
              <span className="app-combobox-selected-name">{selectedApp.appName || 'Unknown app'}</span>
              <span className="app-combobox-selected-fbid">fbId: {selectedApp.fbId || '-'}</span>
            </span>
          ) : (
            <span className="app-combobox-placeholder">All Apps</span>
          )}
        </button>

        {value !== 'all' && (
          <button
            className="app-combobox-clear"
            type="button"
            aria-label="Clear selected app"
            onClick={(e) => {
              e.stopPropagation()
              selectApp(null)
            }}
          >
            ×
          </button>
        )}
      </div>

      {isOpen && (
        <div className="app-combobox-menu">
          <div className="app-combobox-search-wrap">
            <input
              ref={searchInputRef}
              className="form-control app-combobox-search"
              type="text"
              autoComplete="off"
              placeholder="Search by app name or fbId"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setIsOpen(false)
                  setSearch('')
                }
                if (e.key === 'Enter') {
                  e.preventDefault()
                  if (filteredApps.length > 0) selectApp(filteredApps[0])
                }
              }}
            />
          </div>

          <div className="app-combobox-list" role="listbox">
            <button
              className={`app-combobox-option ${value === 'all' ? 'selected' : ''}`}
              type="button"
              role="option"
              aria-selected={value === 'all'}
              onClick={() => selectApp(null)}
            >
              <span className="app-combobox-name">All Apps</span>
            </button>

            {filteredApps.map(app => (
              <button
                className={`app-combobox-option ${String(app.fbId) === String(selectedFbId) ? 'selected' : ''}`}
                key={app.fbId || app.appName}
                type="button"
                role="option"
                aria-selected={String(app.fbId) === String(selectedFbId)}
                onClick={() => selectApp(app)}
              >
                <span className="app-combobox-name">{app.appName || '-'}</span>
                <span className="app-combobox-fbid">fbId: {app.fbId || 'no fbId'}</span>
              </button>
            ))}

            {filteredApps.length === 0 && (
              <div className="app-combobox-empty">No apps found</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}


const setAxiosToken = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
  } else {
    delete axios.defaults.headers.common['Authorization']
  }
}

setAxiosToken(localStorage.getItem(TOKEN_KEY) || '')

if (!window.__AXIOS_401_INTERCEPTOR__) {
  window.__AXIOS_401_INTERCEPTOR__ = true

  axios.interceptors.response.use(
    (res) => res,
    (err) => {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY)
        delete axios.defaults.headers.common['Authorization']
        window.location.reload() // вернет на логин
      }
      return Promise.reject(err)
    }
  )
}


function App() {
  // AUTH (JWT)
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '')
  const [authForm, setAuthForm] = useState({ login: '', password: '' })
  const [authError, setAuthError] = useState('')

  const doLogin = async (e) => {
    e.preventDefault()
    setAuthError('')

    try {
      const res = await axios.post(`${API_URL}/login`, {
        username: authForm.login,
        password: authForm.password
      })

      const t = res.data?.token
      if (!t) {
        setAuthError('No token returned')
        return
      }

      localStorage.setItem(TOKEN_KEY, t)
      setAxiosToken(t)
      setToken(t)
      setAuthForm({ login: '', password: '' })
    } catch (err) {
      setAuthError(err?.response?.data?.error || 'Login failed')
    }
  }

  const doLogout = () => {
    localStorage.removeItem(TOKEN_KEY)
    setAxiosToken('')
    setToken('')
  }

  const [apps, setApps] = useState([])

  // EDIT MODAL
  const [editOpen, setEditOpen] = useState(false)
  const [editForm, setEditForm] = useState({
    appName: '',
    fbId: '',
    appSecret: '',
    appClient: '',
    appId: '',
    referrerDecryptionKey: ''
  })
  const [editMessage, setEditMessage] = useState('')

  const [form, setForm] = useState({
    appName: '',
    fbId: '',
    appSecret: '',
    appClient: '',
    appId: '',
    referrerDecryptionKey: ''
  })
  const [message, setMessage] = useState('')
  const [activeTab, setActiveTab] = useState('apps')

  const [logs, setLogs] = useState([])
  const [filters, setFilters] = useState({
    app: 'all',
    day: new Date().toISOString().split('T')[0],
    event: 'all',
    advertiserId: '',
    fbId: '',
    selectedAppFbId: ''
  })

  useEffect(() => {
    if (!token) return

    axios.get(`${API_URL}/apps`)
      .then(res => setApps(res.data))
      .catch(() => { })
  }, [token])


  const handleAppSubmit = async (e) => {
    e.preventDefault()
    setMessage('')
    try {
      await axios.post(`${API_URL}/apps`, form)
      setMessage('Saved successfully.')

      // FIX: clear ALL fields (so nothing "sticks" after save)
      setForm({
        appName: '',
        fbId: '',
        appSecret: '',
        appClient: '',
        appId: '',
        referrerDecryptionKey: ''
      })

      const res = await axios.get(`${API_URL}/apps`)
      setApps(res.data)
    } catch (err) {
      setMessage('Error saving app.')
    }
  }

  const loadLogs = async () => {
    const effectiveFbId = filters.fbId || filters.selectedAppFbId

    const params = Object.entries(filters).reduce((acc, [key, value]) => {
      if (key === 'selectedAppFbId') return acc
      if (key === 'fbId' && effectiveFbId) {
        acc.fbId = effectiveFbId
        return acc
      }
      if (value !== '' && value !== null && value !== undefined) {
        acc[key] = value
      }
      return acc
    }, {})

    let res
    try {
      res = await axios.get(`${API_URL}/logs`, { params })
    } catch (err) {
      if (!effectiveFbId) throw err

      const fallbackParams = { ...params }
      delete fallbackParams.fbId
      res = await axios.get(`${API_URL}/logs`, { params: fallbackParams })
    }

    const data = Array.isArray(res.data) ? res.data : []
    const fbIdQuery = normalizeValue(effectiveFbId)

    const filteredByFbId = fbIdQuery
      ? data.filter(log => normalizeValue(log.fbId).includes(fbIdQuery))
      : data

    setLogs(filteredByFbId)
  }

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value })
  }

  const openEdit = (app) => {
    setEditMessage('')
    setEditForm({
      appName: app.appName || '',
      fbId: app.fbId || '',
      appSecret: app.appSecret || '',
      appClient: app.appClient || '',
      appId: app.appId || '',
      referrerDecryptionKey: app.referrerDecryptionKey || ''
    })
    setEditOpen(true)
  }

  const closeEdit = () => {
    setEditOpen(false)
    setEditMessage('')
  }

  const saveEdit = async () => {
    setEditMessage('')
    try {
      await axios.post(`${API_URL}/apps`, editForm)

      setEditMessage('Saved.')
      const res = await axios.get(`${API_URL}/apps`)
      setApps(res.data)

      setEditOpen(false)
    } catch (err) {
      setEditMessage('Error saving.')
    }
  }

  if (!token) {
    return (
      <div className="container" style={{ maxWidth: 420, marginTop: '10vh' }}>
        <div className="card shadow-soft">
          <div className="card-body">
            <h2 className="h4 mb-3">Login</h2>

            <form onSubmit={doLogin}>
              <div className="mb-2">
                <input
                  className="form-control"
                  placeholder="Login"
                  value={authForm.login}
                  onChange={e => setAuthForm({ ...authForm, login: e.target.value })}
                />
              </div>

              <div className="mb-2">
                <input
                  className="form-control"
                  type="password"
                  placeholder="Password"
                  value={authForm.password}
                  onChange={e => setAuthForm({ ...authForm, password: e.target.value })}
                />
              </div>

              <button type="submit" className="btn btn-primary w-100">
                Enter
              </button>

              {authError && <div className="text-danger mt-2">{authError}</div>}
            </form>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container py-4" style={{ maxWidth: 1100 }}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h1 className="h3 mb-0 page-title">Facebook App Dashboard</h1>
          <div className="text-muted small">Apps • Logs • Filters</div>
        </div>

        <button className="btn btn-outline-secondary btn-sm" onClick={doLogout} type="button">
          Logout
        </button>
      </div>

      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'apps' ? 'active' : ''}`}
            onClick={() => setActiveTab('apps')}
            type="button"
          >
            App Management
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
            type="button"
          >
            View Logs
          </button>
        </li>
      </ul>

      {activeTab === 'apps' && (
        <>
          <div className="card shadow-soft mb-4">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h2 className="h5 mb-0">Add / Update App</h2>
                {message && <span className="text-muted small">{message}</span>}
              </div>

              <form onSubmit={handleAppSubmit}>
                <div className="row g-2">
                  <div className="col-12">
                    <label className="form-label">App Name</label>
                    <input
                      className="form-control"
                      placeholder="My App"
                      value={form.appName}
                      onChange={e => setForm({ ...form, appName: e.target.value })}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Facebook App ID</label>
                    <input
                      className="form-control"
                      placeholder="123456789"
                      value={form.fbId}
                      onChange={e => setForm({ ...form, fbId: e.target.value })}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Fb Secret</label>
                    <input
                      className="form-control"
                      placeholder="••••••••"
                      value={form.appSecret}
                      onChange={e => setForm({ ...form, appSecret: e.target.value })}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Fb Client</label>
                    <input
                      className="form-control"
                      placeholder="client..."
                      value={form.appClient}
                      onChange={e => setForm({ ...form, appClient: e.target.value })}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Package name</label>
                    <input
                      className="form-control"
                      placeholder="com.example.app"
                      value={form.appId}
                      onChange={e => setForm({ ...form, appId: e.target.value })}
                      required
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label">Referrer Decryption Key</label>
                    <input
                      className="form-control"
                      placeholder="key..."
                      value={form.referrerDecryptionKey}
                      onChange={e => setForm({ ...form, referrerDecryptionKey: e.target.value })}
                      required
                    />
                  </div>

                  <div className="col-12 d-flex gap-2">
                    <button className="btn btn-primary" type="submit">
                      Save App
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setForm({
                        appName: '',
                        fbId: '',
                        appSecret: '',
                        appClient: '',
                        appId: '',
                        referrerDecryptionKey: ''
                      })}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="card shadow-soft mb-4">
            <div className="card-body">
              <h2 className="h5 mb-3">Saved Apps</h2>

              {apps.length === 0 ? (
                <div className="text-muted">No apps yet.</div>
              ) : (
                <ul className="list-group">
                    {apps.map(app => (
                      <li key={app.fbId} className="list-group-item">
                        <div className="d-flex justify-content-between gap-3">
                          <div style={{ minWidth: 0 }}>
                            <div className="fw-semibold text-truncate">{app.appName}</div>
                            <div className="text-muted small text-truncate">fbId: {app.fbId}</div>

                            <details className="mt-2">
                              <summary className="small text-muted" style={{ cursor: 'pointer' }}>
                                Details
                              </summary>

                              <div className="mt-2 small">
                                <div className="d-flex justify-content-between gap-2">
                                  <span className="text-muted">Secret</span>
                                  <span className="text-truncate" style={{ maxWidth: 520 }}>{app.appSecret || '-'}</span>
                                </div>
                                <div className="d-flex justify-content-between gap-2">
                                  <span className="text-muted">Client</span>
                                  <span className="text-truncate" style={{ maxWidth: 520 }}>{app.appClient || '-'}</span>
                                </div>
                                <div className="d-flex justify-content-between gap-2">
                                  <span className="text-muted">Package</span>
                                  <span className="text-truncate" style={{ maxWidth: 520 }}>{app.appId || '-'}</span>
                                </div>
                                <div className="d-flex justify-content-between gap-2">
                                  <span className="text-muted">Decrypt key</span>
                                  <span className="text-truncate" style={{ maxWidth: 520 }}>{app.referrerDecryptionKey || '-'}</span>
                                </div>
                              </div>
                            </details>
                          </div>

                          <div className="d-flex align-items-start gap-2 flex-shrink-0">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              type="button"
                              onClick={() => openEdit(app)}
                            >
                              Edit
                            </button>

                            <button
                              className="btn btn-outline-danger btn-sm"
                              type="button"
                              onClick={async () => {
                                if (window.confirm(`Remove ${app.appName}?`)) {
                                  await axios.delete(`${API_URL}/apps/${app.fbId}`)
                                  const res = await axios.get(`${API_URL}/apps`)
                                  setApps(res.data)
                                }
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}

                </ul>
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === 'logs' && (
        <>
          <div className="card shadow-soft mb-3">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h2 className="h5 mb-0">Filter Logs</h2>
                <span className="text-muted small">{logs.length ? `Loaded: ${logs.length}` : ''}</span>
              </div>

              <div className="logs-filter-grid">
                <div className="logs-filter-field logs-filter-app">
                  <label className="form-label">App</label>
                  <SearchableAppSelect
                    apps={apps}
                    value={filters.app}
                    selectedFbId={filters.selectedAppFbId}
                    onChange={(app) => setFilters(current => ({
                      ...current,
                      app: app ? app.appName : 'all',
                      selectedAppFbId: app ? app.fbId : ''
                    }))}
                  />
                </div>

                <div className="logs-filter-field">
                  <label className="form-label">Day</label>
                  <input className="form-control" type="date" name="day" value={filters.day} onChange={handleFilterChange} />
                </div>

                <div className="logs-filter-field">
                  <label className="form-label">Event</label>
                  <select className="form-select" name="event" value={filters.event} onChange={handleFilterChange}>
                    {eventOptions.map(e => (
                      <option key={e} value={e}>{e}</option>
                    ))}
                  </select>
                </div>

                <div className="logs-filter-field">
                  <label className="form-label">Advertiser ID</label>
                  <input
                    className="form-control"
                    type="text"
                    name="advertiserId"
                    placeholder="Advertiser ID"
                    value={filters.advertiserId}
                    onChange={handleFilterChange}
                  />
                </div>

                <div className="logs-filter-field">
                  <label className="form-label">fbId</label>
                  <input
                    className="form-control"
                    type="text"
                    name="fbId"
                    placeholder="Facebook App ID"
                    value={filters.fbId}
                    onChange={handleFilterChange}
                  />
                </div>

                <div className="logs-filter-actions">
                  <button className="btn btn-primary" onClick={loadLogs} type="button">
                    Load Logs
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    type="button"
                    onClick={() => setFilters({
                      app: 'all',
                      day: new Date().toISOString().split('T')[0],
                      event: 'all',
                      advertiserId: '',
                      fbId: '',
                      selectedAppFbId: ''
                    })}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-soft">
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-sm table-striped table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Time</th>
                      <th>App</th>
                      <th>fbId</th>
                      <th>Advertiser</th>
                      <th>Event</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <tr key={log.id}>
                        <td>{new Date(log.createdAt).toLocaleString()}</td>
                        <td>{log.app}</td>
                        <td className="text-muted">{log.fbId}</td>
                        <td>{log.advertiserId}</td>
                        <td><span className="badge text-bg-secondary">{log.event}</span></td>
                        <td title={log.result} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{log.result}</td>
                      </tr>
                    ))}

                    {logs.length === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center text-muted py-4">
                          No logs loaded yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
      {editOpen && (
        <>
          <div
            className="modal fade show"
            style={{ display: 'block' }}
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit App</h5>
                  <button type="button" className="btn-close" onClick={closeEdit} />
                </div>

                <div className="modal-body">
                  {editMessage && <div className="small text-muted mb-2">{editMessage}</div>}

                  <div className="mb-2">
                    <label className="form-label">App Name</label>
                    <input
                      className="form-control"
                      value={editForm.appName}
                      onChange={e => setEditForm({ ...editForm, appName: e.target.value })}
                    />
                  </div>

                  <div className="mb-2">
                    <label className="form-label">Facebook App ID</label>
                    <input className="form-control" value={editForm.fbId} disabled />
                  </div>

                  <div className="mb-2">
                    <label className="form-label">Fb Secret</label>
                    <input
                      className="form-control"
                      value={editForm.appSecret}
                      onChange={e => setEditForm({ ...editForm, appSecret: e.target.value })}
                    />
                  </div>

                  <div className="mb-2">
                    <label className="form-label">Fb Client</label>
                    <input
                      className="form-control"
                      value={editForm.appClient}
                      onChange={e => setEditForm({ ...editForm, appClient: e.target.value })}
                    />
                  </div>

                  <div className="mb-2">
                    <label className="form-label">Package name</label>
                    <input
                      className="form-control"
                      value={editForm.appId}
                      onChange={e => setEditForm({ ...editForm, appId: e.target.value })}
                    />
                  </div>

                  <div className="mb-2">
                    <label className="form-label">Referrer Decryption Key</label>
                    <input
                      className="form-control"
                      value={editForm.referrerDecryptionKey}
                      onChange={e => setEditForm({ ...editForm, referrerDecryptionKey: e.target.value })}
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={closeEdit}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={saveEdit}>
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show" onClick={closeEdit} />
        </>
      )}

    </div>
  )
}

export default App
